/** @userstory US-01 US-05 US-06 US-34 US-35 */
import apiClient from './apiClient';

// Interface for fetching freelance profile
export interface GetFreelanceProfile {
	id: string;
	profileQualityScore?: number;
	profileQualityActions?: string[];
	talentId?: string;
	firstName: string;
	lastName: string;
	email: string;
	hourlyRate: number;
	employmentTypes: string[];
	openToWork: boolean;
	minAnnualGrossSalary?: number | null;
	availableFrom: string;
	isVisibleOnline: boolean;
	location: string;
	currentTitle: string;
	bioHtml: string;
	whatLookingFor: string;
	personalStatement: string;
	skills: string[];
	tags: string[];
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	/** Business type: "Person" (sole proprietor) or "Company" (corporation) — US-07 */
	businessType?: string;
	/** Owner's personal fiscal code, distinct from company FiscalCode — US-24 */
	billingFiscalCode?: string;
	/** VAT percentage applied on invoices (0-100) — US-12 */
	vatPercentage?: number;
	billingEmail: string;
	certifiedEmail: string;
	iban: string;
	phoneNumber?: string;
	// Media fields
	profilePictureUrl?: string;
	profilePictureThumbnailUrl?: string;
	introductionVideoUrl?: string;
	introductionVideoThumbnailUrl?: string;
	introductionVideoDurationSeconds?: number;
	// Storage fields
	usedStorageBytes?: number;
	maxStorageBytes?: number;
}

// Interface for updating freelance profile (make fields optional to support partial updates)
export interface UpdateFreelanceProfile {
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
	currentTitle?: string;
	hourlyRate?: number;
	employmentTypes?: string[];
	openToWork?: boolean;
	minAnnualGrossSalary?: number | null;
	availableFrom?: string;
	isVisibleOnline?: boolean;
	location?: string;
	bioHtml?: string;
	skills?: string[];
	tags?: string[];
	whatLookingFor?: string;
	personalStatement?: string;
	legalName?: string;
	vatNumber?: string;
	fiscalCode?: string;
	italianSdiCode?: string;
	vatPercentage?: number;
	billingAddress?: string;
	billingCity?: string;
	billingProvince?: string;
	billingZipCode?: string;
	billingCountryCode?: string;
	/** Business type: "Person" or "Company" — US-07 */
	businessType?: string;
	/** Owner's personal fiscal code — US-24 */
	billingFiscalCode?: string;
	billingEmail?: string;
	certifiedEmail?: string;
	iban?: string;
}

const profileService = {
	/**
	 * Fetches the freelance user profile from the backend
	 * @userstory US-01
	 */
	async getFreelanceProfile(): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.get<GetFreelanceProfile>('/api/talentusers/profile');
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch profile data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Updates the freelance user profile
	 * @userstory US-05 US-06
	 */
	async updateFreelanceProfile(payload: UpdateFreelanceProfile): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.put<GetFreelanceProfile>(
				'/api/talentusers/profile',
				payload,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to update profile';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Uploads a CV to the freelance user profile
	 * This operation includes AI-based CV parsing and profile indexing which can take several minutes
	 * @userstory US-34 US-35
	 */
	async uploadCV(file: File): Promise<GetFreelanceProfile> {
		try {
			const formData = new FormData();
			formData.append('file', file);
			const response = await apiClient.post<GetFreelanceProfile>(
				'/api/talentusers/profile/cv',
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					// Extended timeout for AI processing (5 minutes)
					timeout: 300000,
				},
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to upload CV';
			throw new Error(errorMessage);
		}
	},
};

export default profileService;
